import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Briefcase, Users, Heart, Zap, Send } from 'lucide-react';
import { Button } from '../components/ui/button';

const CareersPage = () => {
  const benefits = [
    {
      icon: Heart,
      title: 'Health & Wellness',
      description: 'Comprehensive medical coverage and wellness programs'
    },
    {
      icon: Clock,
      title: 'Flexible Hours',
      description: 'Work-life balance with flexible working arrangements'
    },
    {
      icon: Zap,
      title: 'Growth & Learning',
      description: 'Continuous learning opportunities and career development'
    },
    {
      icon: Users,
      title: 'Great Team',
      description: 'Collaborative environment with passionate colleagues'
    }
  ];

  const positions = [
    {
      id: 1,
      title: 'Customer Service Officer',
      department: 'Customer Experience',
      location: 'Puchong, Selangor',
      type: 'Full-time',
      description: 'Join our customer experience team to provide exceptional support to our students and clients. You will be the first point of contact, helping resolve inquiries and ensuring customer satisfaction.',
      responsibilities: [
        'Respond to customer inquiries via email, chat, and phone',
        'Assist students with course enrollment and technical issues',
        'Process refund requests and handle complaints professionally',
        'Maintain accurate records of customer interactions',
        'Collaborate with other teams to improve customer experience'
      ],
      requirements: [
        'Minimum 1-2 years of customer service experience',
        'Excellent communication skills in English and Bahasa Malaysia',
        'Strong problem-solving abilities',
        'Proficient in using CRM systems',
        'Patient, empathetic, and customer-focused attitude'
      ]
    },
    {
      id: 2,
      title: 'Content Creator',
      department: 'Marketing',
      location: 'Puchong, Selangor',
      type: 'Full-time',
      description: 'Create engaging content that educates and inspires our audience about Chinese Metaphysics. You will work closely with our instructors to develop compelling educational materials across multiple platforms.',
      responsibilities: [
        'Create engaging social media content (posts, reels, stories)',
        'Write blog articles and educational content',
        'Assist in video production and editing',
        'Develop graphics and visual content using design tools',
        'Research trending topics in personal development and metaphysics'
      ],
      requirements: [
        'Portfolio showcasing content creation work',
        'Experience with social media platforms (Instagram, TikTok, YouTube)',
        'Proficiency in Canva, Adobe Creative Suite, or similar tools',
        'Strong writing and storytelling skills',
        'Interest in Chinese Metaphysics and personal development'
      ]
    },
    {
      id: 3,
      title: 'Digital Marketing Executive',
      department: 'Marketing',
      location: 'Puchong, Selangor',
      type: 'Full-time',
      description: 'Drive our digital marketing initiatives to grow our student base and brand awareness. You will manage campaigns across multiple channels and analyze performance to optimize results.',
      responsibilities: [
        'Plan and execute digital marketing campaigns (Facebook, Google, etc.)',
        'Manage email marketing automation and sequences',
        'Analyze campaign performance and prepare reports',
        'Optimize landing pages for conversion',
        'Collaborate with content team on marketing materials'
      ],
      requirements: [
        'Minimum 2-3 years of digital marketing experience',
        'Experience with Facebook Ads Manager and Google Ads',
        'Knowledge of email marketing platforms (Mailchimp, ActiveCampaign)',
        'Strong analytical skills and data-driven mindset',
        'Bachelor\'s degree in Marketing, Business, or related field'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-950 pt-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center text-gray-400 hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Join Our Team</h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Help us empower individuals worldwide through the wisdom of Chinese Metaphysics. 
            We're looking for passionate people to join our growing team.
          </p>
        </div>

        {/* Benefits */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Why Work With Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-400 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Open Positions */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-8">Open Positions</h2>
          <div className="space-y-6">
            {positions.map((position) => (
              <div key={position.id} className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{position.title}</h3>
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        <span className="flex items-center gap-1 text-gray-400">
                          <Briefcase className="w-4 h-4" />
                          {position.department}
                        </span>
                        <span className="flex items-center gap-1 text-gray-400">
                          <MapPin className="w-4 h-4" />
                          {position.location}
                        </span>
                        <span className="px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded text-xs">
                          {position.type}
                        </span>
                      </div>
                    </div>
                    <a 
                      href={`mailto:team@digitaldivecoaching.com?subject=Application for ${position.title}`}
                      className="inline-flex items-center px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white font-medium rounded-lg transition-colors"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Apply Now
                    </a>
                  </div>

                  {/* Description */}
                  <p className="text-gray-300 mb-6">{position.description}</p>

                  {/* Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-white font-semibold mb-3">Responsibilities</h4>
                      <ul className="space-y-2">
                        {position.responsibilities.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-gray-400 text-sm">
                            <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 shrink-0"></span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-3">Requirements</h4>
                      <ul className="space-y-2">
                        {position.requirements.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-gray-400 text-sm">
                            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full mt-2 shrink-0"></span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-16 text-center">
          <div className="bg-gradient-to-r from-amber-500/10 to-purple-500/10 border border-gray-800 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Don't See a Suitable Position?</h2>
            <p className="text-gray-400 mb-6">
              We're always looking for talented individuals. Send us your resume and let us know how you can contribute.
            </p>
            <a 
              href="mailto:team@digitaldivecoaching.com?subject=General Application"
              className="inline-flex items-center px-6 py-3 bg-amber-600 hover:bg-amber-500 text-white font-medium rounded-lg transition-colors"
            >
              Send Your Resume
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CareersPage;
